import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialCodeSelect } from '@/components/UI/GeoSelect';
import { useGetCountriesQuery } from '@/redux/internal-services/geoApi';
import { splitPhone, normalizeDialCode } from '@/utils/phone';
import { apiErrorMessage } from '@/utils/apiError';
import {
  useListInvitationsQuery,
  useSendInvitationMutation,
  useListTenantRolesQuery,
} from '@/redux/internal-services/onboardingApi';
import { BackIcon } from './StepShell';
import type { StepHandle, SubmitResult } from './stepHandle';
import { Button } from '@/components/UI/Button';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';

interface Props {
  onBack?: () => void;
  onLaunch: () => void;
  onSkip?: () => void;
  launching?: boolean;
  disabled?: boolean;
}

interface InvitationRow {
  invitation_id?: string;
  invitee_name?: string;
  invitee_email?: string;
  roles?: string[];
  status?: string;
}

/**
 * Guided-setup "Team Onboarding" step — invite team members (reuses the invitations API) and
 * launch the dashboard. Each invite is sent immediately; "Launch Dashboard" finalises setup.
 */
const TeamOnboardingStep = forwardRef<StepHandle, Props>(({ onBack, onLaunch, onSkip, launching, disabled = false }, ref) => {
  const { t } = useTranslation();
  const { data: invitationsData } = useListInvitationsQuery();
  const { data: tenantRolesData } = useListTenantRolesQuery();
  const { data: countriesData } = useGetCountriesQuery();
  const [sendInvitation, { isLoading: sending }] = useSendInvitationMutation();

  const invitations = (invitationsData?.data ?? []) as InvitationRow[];
  const roles = useMemo(
    () => (tenantRolesData?.data ?? []).filter((r) => r.visibility),
    [tenantRolesData]
  );
  const roleName = (id: string) => roles.find((r) => r.tenant_role_id === id)?.name ?? id;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phone, setPhone] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [error, setError] = useState('');

  // Synchronous mirror so voice can fill the draft fields one-by-one and then add, reading the
  // freshest values even if the actuators land in the same tick.
  const draftRef = useRef({ name, email, phone, roleIds });
  useEffect(() => { draftRef.current = { name, email, phone, roleIds }; }, [name, email, phone, roleIds]);

  const toggleRole = (id: string) =>
    setRoleIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  // Send an invitation from the current draft. Shared by the visible button and the voice agent.
  const sendCurrent = async (): Promise<SubmitResult> => {
    const d = draftRef.current;
    if (!d.email.trim()) {
      setError(t('onboarding.steps.emailRequired'));
      return { ok: false, reason: 'validation', message: 'Email address is required.' };
    }
    if (d.roleIds.length === 0) {
      setError(t('onboarding.steps.selectAtLeastOneRole'));
      return { ok: false, reason: 'validation', message: 'At least one role is required.' };
    }
    setError('');
    try {
      await sendInvitation({
        invitee_name: d.name.trim() || undefined,
        invitee_email: d.email.trim(),
        invitee_phone: d.phone.trim() ? `${countryCode}${d.phone.trim()}` : undefined,
        roles: d.roleIds,
      }).unwrap();
      draftRef.current = { name: '', email: '', phone: '', roleIds: [] };
      setName('');
      setEmail('');
      setPhone('');
      setRoleIds([]);
      return { ok: true };
    } catch (err) {
      setError(apiErrorMessage(err, t, 'Failed to add team member. Please try again.'));
  return { ok: false, reason: 'transient', message: 'Failed to add team member.' };
    }
  };

  const handleAdd = () => { void sendCurrent(); };

  const dialCodes = countriesData?.data?.map((c) => c.dial_code) ?? [];

  // Voice actuators — fill the visible draft fields one-by-one, then add via addTeamMember().
  const setFieldById = (id: string, value: string) => {
    if (id === 'name') { draftRef.current = { ...draftRef.current, name: value }; setName(value); }
    else if (id === 'email') { draftRef.current = { ...draftRef.current, email: value }; setEmail(value); }
    else if (id === 'phoneCountryCode') {
      // Agent set the dial code separately → drive the dropdown directly.
      const norm = normalizeDialCode(value);
      if (norm) setCountryCode(norm);
    }
    else if (id === 'phone') {
      // Agent passes the local number (or a full international one as a fallback). Separate the
      // country code from the local part so the dropdown + number field are populated correctly.
      const { countryCode: cc, number: num } = splitPhone(value, dialCodes);
      if (cc) setCountryCode(cc);
      draftRef.current = { ...draftRef.current, phone: num };
      setPhone(num);
    }
  };

  const setRole = (rName: string, selected: boolean) => {
    const id = roles.find((r) => r.name.toLowerCase() === rName.trim().toLowerCase())?.tenant_role_id;
    if (!id) { setError(`Unknown role "${rName}".`); return; }
    const has = draftRef.current.roleIds.includes(id);
    const next = selected
      ? (has ? draftRef.current.roleIds : [...draftRef.current.roleIds, id])
      : draftRef.current.roleIds.filter((r) => r !== id);
    draftRef.current = { ...draftRef.current, roleIds: next };
    setRoleIds(next);
  };

  useImperativeHandle(ref, (): StepHandle => ({
    getState: () => ({
      invitations: invitations.map((i) => ({ name: i.invitee_name, email: i.invitee_email, roles: (i.roles ?? []).map(roleName) })),
      availableRoles: roles.map((r) => r.name),
      draft: { name, email, countryCode, phone, roleIds: roleIds.map(roleName) },
    }),
    // Invitations are sent as they're added, so there's nothing to persist here. Finishing the
    // wizard (launch) is handled by the page's nav.goNext/nav.launch, not by submit, to avoid a
    // double navigation when the agent says "next" on the final step.
    submit: async () => ({ ok: true }),
    setField: setFieldById,
    setRole,
    addTeamMember: sendCurrent,
  }));

  const inputCls = fieldClass();

  return (

    <div className={`card-surface p-4 sm:p-8${disabled ? ' pointer-events-none opacity-60 select-none' : ''}`}>
      <h2 className="text-h3 text-ui-text mb-1">Team Onboarding</h2>
      <p className="text-ui-text2 text-sm mb-6">Final step — invite your team members</p>


      {invitations.length > 0 && (
        <div className="mb-6">
          <p className="text-sm font-medium text-ui-text mb-2">Added Team Members</p>
          <div className="space-y-2">
            {invitations.map((inv, idx) => (
              <div key={inv.invitation_id ?? idx} className="flex items-center justify-between border border-ui-border rounded-card px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-ui-text">{inv.invitee_name || inv.invitee_email}</p>
                  {inv.invitee_name && <p className="text-xs text-ui-text3">{inv.invitee_email}</p>}
                </div>
                {inv.roles && inv.roles.length > 0 && (
                  <div className="flex flex-wrap gap-1 justify-end">
                    {inv.roles.map((rid) => (
                      <span key={rid} className="px-2.5 py-1 bg-accent-soft text-accent-text text-xs font-medium rounded-full">
                        {roleName(rid)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-sm font-medium text-ui-text mb-3">Add new Team member</p>

      <div className="mb-3">
        <label className="block text-sm text-ui-text2 mb-1.5">Name</label>
        <input type="text" placeholder="Enter team member name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
      </div>

      <div className="mb-3">
        <label className="block text-sm text-ui-text2 mb-1.5">Email Address</label>
        <input type="email" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
      </div>

      <div className="mb-3">
        <label className="block text-sm text-ui-text2 mb-1.5">Mobile Phone Number</label>
        <div className="flex gap-2">
          <DialCodeSelect value={countryCode} onChange={setCountryCode} className="w-28 sm:w-32" />
          <input type="tel" placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} className={cn(fieldClass(), 'min-w-0 flex-1')} />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm text-ui-text2 mb-1.5">Roles</label>
        <div className="flex flex-wrap gap-2">
          {roles.map((r) => (
            <button
              key={r.tenant_role_id}
              type="button"
              onClick={() => toggleRole(r.tenant_role_id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                roleIds.includes(r.tenant_role_id) ? 'bg-accent-fill text-on-accent' : 'bg-ui-subtle text-ui-text2 hover:bg-ui-hover'
              }`}
            >
              {r.name}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-danger-text text-sm mb-3">{error}</p>}

      <Button type="button"
        onClick={handleAdd}
        disabled={sending}
        variant="secondary" size="lg" className="w-full mb-6">
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
        </svg>
        {sending ? 'Adding...' : 'Add Team Member'}
      </Button>

      <div className="flex gap-3">
        {onBack && (
          <Button type="button" onClick={onBack} variant="outline" size="lg" className="flex-1">
            <BackIcon />
            Back
          </Button>
        )}
        <Button type="button"
          onClick={onLaunch}
          disabled={launching}
          size="lg" className="flex-1">
          {launching ? 'Saving...' : 'Next'}
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </Button>
      </div>

      {onSkip && (
        <Button type="button" onClick={onSkip} variant="link" className="w-full mt-3">
          Skip this step
        </Button>
      )}
    </div>
  );
});

TeamOnboardingStep.displayName = 'TeamOnboardingStep';

export default TeamOnboardingStep;
